/* eslint-disable no-useless-escape */
import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';

import {
  Row,
  Col,
  Input,
  Select,
} from 'antd';
import SimpleReactValidator from 'simple-react-validator';
import {
  validators,
} from 'utils/validationMessages';

import { MESSAGES } from 'appconstants';

import {
  SpForm,
  SpError,
  SpTextMute,
  SpText,
} from 'components/DesignKit';

const { Option } = Select;

type Props = {
  isIndia: boolean,
}

const BusinessBankDetails = (props: Props, ref) => {
  const { isIndia } = props;

  const [, forceUpdate] = useState();
  const [bankName, setBankName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [bankRoutingNumber, setBankRoutingNumber] = useState('');
  const [ifscNumber, setIfscNumber] = useState('');
  const [accountType, setAccountType] = useState();
  // const [voidCheckUrl, setVoidCheckUrl] = useState('');
  const accountRegex = /[0-9]{6,17}/;
  const routingRegex = /[0-9]{9}/;
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const averagemonthlyVolumeHandler = useRef();

  const simpleValidator = useRef(new SimpleReactValidator({
    validators: {
      confirmAccount: {
        message: MESSAGES.BANK_DETAILS.MATCH,
        rule: (val, params) => val === params[0],
      },
      accountNumber: {
        message: MESSAGES.BANK_DETAILS.VALID,
        rule: val => accountRegex.test(val),
      },
      bankIFSC: {
        message: MESSAGES.BANK_DETAILS.IFSC,
        rule: val => ifscRegex.test(val),
      },
      bankRoutingNumber: {
        message: MESSAGES.BANK_DETAILS.ROUTING,
        rule: val => routingRegex.test(val),
      },
      bankNameValidation: {
        message: validators.bussiness.validBankName,
        rule: val => /^[A-Za-z0-9&,\s.\-'\$@_]{1,50}$/.test(val),
      },
      accountName: {
        message: validators.bussiness.validBankAccountName,
        // eslint-disable-next-line no-useless-escape
        rule: val => /^[A-Za-z0-9&,\s.\-'\$@_]{1,50}$/.test(val),
      },
      averageTicketAmountCheck: {
        message: 'Average ticket amount should be less than average monthly amount',
        rule: (val) => Number(averagemonthlyVolumeHandler?.current?.input?.value.replaceAll(',', '')) > Number(val ? val.replaceAll(',', '') : 0.00),
      },

    },
  }));

  simpleValidator.current.purgeFields();

  useImperativeHandle(ref, () => ({
    validate: () => {
      const formValid = simpleValidator.current.allValid();
      if (!formValid) {
        simpleValidator.current.showMessages();
        forceUpdate(1);
        return false;
      }
      return true;
    },
    getValues: () => ({
      bankName,
      accountHolderName,
      accountNumber,
      confirmAccountNumber,
      bankRoutingNumber,
      accountType,
      ifscNumber,
      // voidCheckUrl,
    }),
  }));

  return (
    <>
      <Row className="my-2 mb-3">
        <Col>
          <SpText fontSize="20px" fontWeight="600">
            Manage Bank Information
          </SpText>
        </Col>
      </Row>
      <SpForm>

        <Row type="flex" justify="start" align="middle">
          <Col span={8}>
            <span>Account Holder Name</span>
          </Col>
          <Col span={16}>
            <Input
              placeholder="Name of the account holder"
              value={accountHolderName}
              onBlur={() => setAccountHolderName(accountHolderName && accountHolderName.trim())}
              onChange={e => setAccountHolderName(e.currentTarget.value)}
            />
            <SpError>
              {simpleValidator.current.message('Account Holder Name', accountHolderName, 'required|accountName')}
            </SpError>
          </Col>
          <Col span={24}>
            <SpTextMute>
              Name should match the legal business name or legal
              name of the individual or sole proprietor
            </SpTextMute>
          </Col>
        </Row>

        <Row type="flex" justify="start" align="middle">
          <Col span={8}>
            <span>Bank Name</span>
          </Col>
          <Col span={16}>
            <Input
              placeholder="Bank Name"
              value={bankName}
              onBlur={() => setBankName(bankName && bankName.trim())}
              onChange={e => setBankName(e.currentTarget.value)}
            />
            <SpError>
              {simpleValidator.current.message('Bank name', bankName, 'required|bankNameValidation')}
            </SpError>
          </Col>
        </Row>

        {
          !isIndia && (
            <Row type="flex" justify="start" align="middle">
              <Col span={8}>
                <span>Account Type</span>
              </Col>
              <Col span={16}>
                <Select
                  placeholder="Select Account Type"
                  optionFilterProp="children"
                  className="w-100"
                  showSearch
                  value={accountType}
                  onChange={(value) => {
                    setAccountType(value);
                  }}
                >
                  <Option key={1} value="SAVINGS">SAVINGS</Option>
                  <Option key={2} value="CHECKING">CHECKING</Option>
                </Select>
                <SpError>
                  {simpleValidator.current.message('account Type', accountType, 'required')}
                </SpError>
              </Col>
            </Row>
          )
        }

        <Row type="flex" justify="start" align="middle">
          <Col span={8}>
            <span>Account Number</span>
          </Col>
          <Col span={16}>
            <Input
              placeholder="Bank account number"
              value={accountNumber}
              onBlur={() => setAccountNumber(accountNumber && accountNumber.trim())}
              onChange={e => setAccountNumber(e.currentTarget.value)}
              onPaste={e => e.preventDefault()}
            />
            <SpError>
              {simpleValidator.current.message('Account Number', accountNumber, 'required|accountNumber')}
            </SpError>
          </Col>
        </Row>

        <Row type="flex" justify="start" align="middle">
          <Col span={8}>
            <span>Confirm Account No.</span>
          </Col>
          <Col span={16}>
            <Input
              placeholder="Confirm bank account number"
              value={confirmAccountNumber}
              onBlur={() => setConfirmAccountNumber(confirmAccountNumber && confirmAccountNumber.trim())}
              onChange={e => setConfirmAccountNumber(e.currentTarget.value)}
              onPaste={e => e.preventDefault()}
            />
            <SpError>
              {simpleValidator.current.message('Confirm Account Number', confirmAccountNumber, `required|confirmAccount:${accountNumber}`)}
            </SpError>
          </Col>
        </Row>

        {
          !isIndia && (
            <>
              <Row type="flex" justify="start" align="middle">
                <Col span={8}>
                  <span>Routing Number</span>
                </Col>
                <Col span={16}>
                  <Input
                    placeholder="Routing number"
                    value={bankRoutingNumber}
                    onBlur={() => setBankRoutingNumber(bankRoutingNumber && bankRoutingNumber.trim())}
                    onChange={e => setBankRoutingNumber(e.currentTarget.value)}
                  />
                  <SpError>
                    {simpleValidator.current.message('Bank Routing Number', bankRoutingNumber, 'required|bankRoutingNumber')}
                  </SpError>
                </Col>
              </Row>
            </>
          )
        }

        {
          isIndia && (
            <>
              <Row type="flex" justify="start" align="middle">
                <Col span={8}>
                  <span>IFSC Number</span>
                </Col>
                <Col span={16}>
                  <Input
                    placeholder="IFSC number"
                    value={ifscNumber}
                    onBlur={() => setIfscNumber(ifscNumber && ifscNumber.trim())}
                    onChange={e => setIfscNumber((e.currentTarget.value).toUpperCase())}
                  />
                  <SpError>
                    {simpleValidator.current.message('ifsc Number', ifscNumber, 'required|bankIFSC',
                      { messages: { required: 'The IFSC Number field is required.' } })}
                  </SpError>
                </Col>
              </Row>
            </>
          )
        }
      </SpForm>
    </>
  );
};

// $FlowFixMe
export default forwardRef(BusinessBankDetails);
